import { useCallback } from 'react';
import type { MouseEvent, PropsWithChildren } from 'react';
import { Link, useLoaderData } from '@remix-run/react';
import { track } from '~/services/analytics/index.client.ts';
import type { AnalyticsData } from '~/utils/analytics';
import type { loader as rootLoader } from '~/routes/_index.ts';
import type { RemixLinkProps } from '@remix-run/react/dist/components';
import { isExternalUrlParam } from '~/utils/is-external-url-param';
import { addParametersToURL } from '~/services/links/index';
import {
  dispatchAudioPlayerOpen,
  getIsAudioPlayerLink,
  parseAudioPlayerLink,
} from '~/utils/audio-player';

export const MaybeLink = (
  props: PropsWithChildren<{
    to?: string;
    analyticsdata?: AnalyticsData;
    enabled?: boolean;
    linkariainfo?: string;
    linkPrefetchSwitch?: boolean;
  }>
) => {
  const { deviceType, layoutIdOverride } = useLoaderData<typeof rootLoader>();
  const propsTo = props.to;

  const isVideo = typeof propsTo === 'string' && propsTo?.startsWith('/player');
  const isAudioPlayer = getIsAudioPlayerLink(propsTo);

  const onClickLink = useCallback(
    (event?: MouseEvent<HTMLAnchorElement>) => {
      const { eventName, trackData } = props?.analyticsdata || {};
      if (eventName && trackData) {
        track(eventName, trackData);
      }

      if (!propsTo || !getIsAudioPlayerLink(propsTo)) return;

      event?.preventDefault();

      const audioPlayerDetail = parseAudioPlayerLink(propsTo);

      if (!audioPlayerDetail) return;
      dispatchAudioPlayerOpen(audioPlayerDetail);
    },
    [props?.analyticsdata, propsTo]
  );

  if (!propsTo || props.enabled === false) return <>{props.children}</>;

  const hasExternalUrl = isExternalUrlParam({ link: { href: propsTo } });

  const isExternalLink: boolean = propsTo?.startsWith('external-link::');

  const toWithLayoutIdOverride: string =
    layoutIdOverride && propsTo.startsWith('/')
      ? addParametersToURL(propsTo, { layoutId: layoutIdOverride })
      : propsTo;

  if (!isExternalLink)
    return (
      <Link
        target={hasExternalUrl ? '_blank' : '_self'}
        onClick={onClickLink}
        aria-label={props.linkariainfo}
        title={props.linkariainfo}
        to={isAudioPlayer ? '#' : toWithLayoutIdOverride}
        reloadDocument={isVideo}
        prefetch={getPrefetchBehavior(
          isVideo || isAudioPlayer,
          deviceType,
          props?.linkPrefetchSwitch
        )}
      >
        {props.children}
      </Link>
    );

  const externalLink = propsTo.slice(15);

  return (
    <>
      <a
        href={externalLink || propsTo}
        aria-label={props.linkariainfo}
        target="_blank"
        rel="noreferrer"
      >
        {props.children}
      </a>
    </>
  );
};

function getPrefetchBehavior(
  isVideo: boolean,
  deviceType: string,
  linkPrefetchSwitch?: boolean
): RemixLinkProps['prefetch'] {
  if (isVideo || !linkPrefetchSwitch) return 'none';

  if (deviceType === 'desktop') return 'intent';

  return 'viewport';
}
